import type { BatchUpdateAreasDto, BatchUpdateAreasResult } from './types/resource-area';
import { createAppRequest } from "@/services/request";
import type { ApiRequestOptions } from "./types/common";
import type {
  AssignOpportunityDto,
  BatchAssignOpportunitiesDto,
  OpportunityDrawerProfile,
  OpportunityPageResult,
  OpportunityDrawerSummaryPanel,
  OpportunityItem,
  OpportunityListStatistics,
  OpportunityQueryParams,
  OpportunityAiScoreFeedback,
  OpportunityProjectionResponse,
  DailyOpportunityFocusResponse,
  UpdateOpportunityPayload,
  OpportunityViewMode,
} from "./types/opportunity";

const request = createAppRequest();
const BASE_PATH = "/bff/zy-crm/opportunities";

type DetailScopeOptions = ApiRequestOptions & {
  params?: Record<string, unknown>;
  viewMode?: OpportunityViewMode;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const EMPTY_OPPORTUNITY_STATISTICS: OpportunityListStatistics = {
  totalAmount: '0.000000',
  quotedAmount: '0.000000',
  dealAmount: '0.000000',
  lostAmount: '0.000000',
  dueActionCount: 0,
  stalledCount: 0,
  highRiskCount: 0,
  unavailableMetrics: [],
};

const normalizeOpportunityPage = (resp: unknown): OpportunityPageResult => {
  if (!isObject(resp)) {
    throw new Error('商机列表返回结构异常，请检查 API 地址或代理配置');
  }

  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw =
      (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;

    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data as OpportunityItem[],
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
        statistics: isObject(pageObj.statistics)
          ? {
              ...EMPTY_OPPORTUNITY_STATISTICS,
              ...(pageObj.statistics as unknown as OpportunityListStatistics),
            }
          : EMPTY_OPPORTUNITY_STATISTICS,
      };
    }
  }

  throw new Error('商机列表返回结构异常，请检查 API 地址或代理配置');
};

export async function queryOpportunities(
  params: OpportunityQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'GET',
    params: {
      ...params,
      tagUuids: Array.isArray(params.tagUuids)
        ? params.tagUuids.join(",")
        : params.tagUuids,
    },
    ...(options || {}),
  });
  return normalizeOpportunityPage(resp);
}

const unwrapOpportunityDetail = (resp: unknown): OpportunityItem => {
  if (resp && typeof resp === "object") {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== "object") continue;
      const record = candidate as Record<string, unknown>;
      if (typeof record.uuid === "string") {
        return candidate as OpportunityItem;
      }
    }
  }
  throw new Error("商机详情返回结构异常");
};

export async function queryOpportunityDetail(
  uuid: string,
  options?: DetailScopeOptions
) {
  const { viewMode, params, ...rest } = options || {};
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: "GET",
    params: {
      ...(params || {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
  return unwrapOpportunityDetail(resp);
}

export async function updateOpportunity(
  uuid: string,
  payload: UpdateOpportunityPayload,
  options?: ApiRequestOptions
) {
  return request<OpportunityItem>(`${BASE_PATH}/${uuid}`, {
    method: "PATCH",
    data: payload,
    ...(options || {}),
  });
}

export async function touchOpportunity(uuid: string, options?: ApiRequestOptions) {
  return request<OpportunityItem>(`${BASE_PATH}/${uuid}/touch`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function recalculateOpportunityAiScore(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<OpportunityItem>(`${BASE_PATH}/${uuid}/ai-score/recalculate`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function submitOpportunityAiScoreFeedback(
  uuid: string,
  payload: OpportunityAiScoreFeedback,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/ai-score/feedback`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function refreshOpportunityProjections(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<OpportunityProjectionResponse>(
    `${BASE_PATH}/${uuid}/projections/refresh`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function queryDailyOpportunityFocus(options?: ApiRequestOptions) {
  return request<DailyOpportunityFocusResponse>(
    '/bff/zy-crm/opportunity-assistant/daily-focus',
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function regenerateDailyOpportunityFocus(
  options?: ApiRequestOptions,
) {
  return request<DailyOpportunityFocusResponse>(
    '/bff/zy-crm/opportunity-assistant/daily-focus/regenerate',
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function putOpportunityToPool(
  uuid: string,
  options?: ApiRequestOptions
) {
  return request<OpportunityItem>(`${BASE_PATH}/${uuid}/pool`, {
    method: "POST",
    data: {},
    ...(options || {}),
  });
}

export async function claimOpportunity(
  uuid: string,
  options?: ApiRequestOptions
) {
  return request<OpportunityItem>(`${BASE_PATH}/${uuid}/claim`, {
    method: "POST",
    ...(options || {}),
  });
}

export async function assignOpportunity(
  uuid: string,
  payload: AssignOpportunityDto,
  options?: ApiRequestOptions
) {
  return request<OpportunityItem>(`${BASE_PATH}/${uuid}/assign`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function batchAssignOpportunities(
  payload: BatchAssignOpportunitiesDto,
  options?: ApiRequestOptions
) {
  return request<{ uuids: string[]; memberUuid: string }>(
    `${BASE_PATH}/batch-assign`,
    {
      method: "POST",
      data: payload,
      ...(options || {}),
    }
  );
}

export async function followOpportunity(
  uuid: string,
  viewMode: OpportunityViewMode,
  options?: ApiRequestOptions,
) {
  return request<{ uuid: string; isFollowed: true }>(`${BASE_PATH}/${uuid}/follow`, {
    method: 'POST',
    params: { viewMode },
    ...(options || {}),
  });
}

export async function unfollowOpportunity(uuid: string, options?: ApiRequestOptions) {
  return request<{ uuid: string; isFollowed: false }>(`${BASE_PATH}/${uuid}/follow`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function queryOpportunityDrawerProfile(
  uuid: string,
  options?: DetailScopeOptions
) {
  const { viewMode, params, ...rest } = options || {};
  return request<OpportunityDrawerProfile>(
    `${BASE_PATH}/${uuid}/drawer-profile`,
    {
      method: "GET",
      params: {
        ...(params || {}),
        ...(viewMode ? { viewMode } : {}),
      },
      ...rest,
    }
  );
}

export async function createOpportunityTeamMember(
  uuid: string,
  payload: { memberUuid: string; roleCode?: string; role?: string },
  options?: ApiRequestOptions
) {
  return request(`${BASE_PATH}/${uuid}/team-members`, {
    method: "POST",
    data: payload,
    ...(options || {}),
  });
}

export async function deleteOpportunityTeamMember(
  uuid: string,
  teamMemberUuid: string,
  options?: ApiRequestOptions
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: "DELETE",
    ...(options || {}),
  });
}

export async function batchUpdateOpportunityAreas(payload: BatchUpdateAreasDto, options?: ApiRequestOptions) {
  return request<BatchUpdateAreasResult>(`${BASE_PATH}/batch-areas`, {
    method: 'PATCH', data: payload, ...(options || {}),
  });
}

/** Convert once; retried requests return the same lead UUID. */
export async function convertOpportunityToLead(uuid: string, payload: { reason: string }) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}/convert-to-lead`, {
    method: 'POST', data: payload,
  });
  const root = resp as Record<string, unknown> | null;
  for (const candidate of [root, root?.data, root?.result, root?.payload]) {
    if (isObject(candidate) && typeof candidate.leadUuid === 'string' && typeof candidate.opportunityUuid === 'string') {
      return candidate as { opportunityUuid: string; leadUuid: string };
    }
  }
  throw new Error('转换结果异常，请刷新商机详情确认转换状态');
}
